import React from 'react';
import { ArrowLeft, CalendarClock, Hash, Tent } from 'lucide-react';
import CopyButton from './CopyButton';

const StatusBadge = ({ refunded, children }) => (
    <span className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${refunded ? 'tw-bg-blue-50 tw-text-blue-700' : 'tw-bg-red-50 tw-text-red-700'}`}>
        {children}
    </span>
);

const MetaItem = ({ icon: Icon, label, children }) => (
    <div className="tw-flex tw-items-center tw-gap-1.5">
        <Icon className="tw-h-3.5 tw-w-3.5 tw-text-neutral-400" aria-hidden="true" />
        <span className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400">{label}</span>
        <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-neutral-800">{children}</span>
    </div>
);

/**
 * Top bar of the booking registration page.
 *
 * `primaryAction` sits in the meta row, before the booking number (Manage Registration in the
 * design). On the right, as in the design: `navigation` (Prev / Next) at the top, level with Back,
 * and `actions` (Activity, Contact Support) at the bottom, level with the meta row.
 *
 * A div, not <header>: main.css gives every <header> the site header's min-height.
 */
const BookingHeader = ({ programName, programCode, campName, bookingNumber, bookingDate, isCanceled, isRefunded, onBack, primaryAction, navigation, actions }) => (
    <div className="booking-detail-page tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-5 tw-py-4 md:tw-flex-row md:tw-items-start md:tw-justify-between">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
            {/* goBack, not a fixed route: this page is opened from eight different screens. */}
            <button type="button" onClick={onBack} className="tw-inline-flex tw-w-fit tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-brand-600 hover:tw-text-brand-700">
                <ArrowLeft className="tw-h-4 tw-w-4" aria-hidden="true" /> Back
            </button>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <h1 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900">{programName}</h1>
                {programCode ? <span className="tw-rounded tw-bg-neutral-100 tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-xs tw-text-neutral-600">{programCode}</span> : null}
                {isCanceled ? <StatusBadge>Canceled</StatusBadge> : null}
                {isRefunded ? <StatusBadge refunded>Refunded</StatusBadge> : null}
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2">
                {primaryAction}
                <MetaItem icon={Hash} label="Booking number">
                    {bookingNumber}
                    <CopyButton value={bookingNumber} label="booking number" />
                </MetaItem>
                <MetaItem icon={CalendarClock} label="Booking date">{bookingDate}</MetaItem>
                {campName ? <MetaItem icon={Tent} label="Camp">{campName}</MetaItem> : null}
            </div>
        </div>

        {navigation || actions
            ? <div className="tw-flex tw-flex-col tw-items-end tw-gap-3 md:tw-justify-between md:tw-self-stretch">
                {navigation || <span aria-hidden="true" />}
                {actions ? <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{actions}</div> : null}
            </div>
            : null}
    </div>
);

export default BookingHeader;
